"use client";

import type { ShowAttendanceStatus } from "@workspace/types";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { trackShowAttendance } from "@/lib/services/analytics";
import { createClient } from "@/lib/supabase/client";
import { trpc } from "@/lib/trpc/react";

/**
 * Real-time show attendance hook
 *
 * Modern architecture:
 * - Database is the single source of truth
 * - Real-time events drive all updates
 * - Optimistic updates for instant feedback
 * - No cache invalidation needed (staleTime: Infinity)
 * - Automatic cross-tab/device synchronization
 */

// Custom event for syncing optimistic updates across component instances
const SHOW_ATTENDANCE_EVENT = "show-attendance-update";

interface ShowAttendanceData {
	userId: string;
	showId: string;
	status: ShowAttendanceStatus;
	autoMarked?: boolean;
}

interface UseRealtimeShowAttendanceOptions {
	showId: string;
	showName?: string;
	venue?: string;
	userId: string | null;
	enabled?: boolean;
}

export function useRealtimeShowAttendance({
	showId,
	showName,
	venue,
	userId,
	enabled = true,
}: UseRealtimeShowAttendanceOptions) {
	// Local state is our source of truth
	const [status, setStatus] = useState<ShowAttendanceStatus | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const channelRef = useRef<any>(null);
	const instanceId = useRef(Math.random().toString(36).substring(7));
	const supabase = createClient();
	const utils = trpc.useUtils();

	// tRPC mutation for updating attendance
	const updateAttendanceMutation = trpc.show.updateAttendance.useMutation({
		onSuccess: async () => {
			// Invalidate show facepile data for immediate updates
			await utils.show.getFacepileData.invalidate();

			// Invalidate show discovery cache to update facepiles
			await utils.show.list.invalidate();

			// Invalidate profile caches with refetchType: 'all' to force refetch
			// on mounted components (SPA tabs stay mounted)
			await utils.user.getProfileShows.invalidate(
				undefined,
				{ refetchType: 'all' }
			);

			toast.success("Show attendance updated");
		},
		onError: (err) => {
			// On error, real-time will correct the state
			const errorMessage = err.message?.includes("UNAUTHORIZED")
				? "Please sign in to update show attendance"
				: err.message?.includes("Rate limit")
					? err.message
					: "Failed to update show attendance";

			toast.error(errorMessage);
		},
	});

	// Initial data fetch - happens ONCE, never goes stale
	useEffect(() => {
		if (!userId || !showId || !enabled) {
			setIsLoading(false);
			setStatus(null);
			return;
		}

		async function fetchInitialStatus() {
			if (!userId) return;

			try {
				const { data, error } = await supabase
					.from("show_attendance")
					.select("status")
					.eq("user_id", userId)
					.eq("show_id", showId)
					.maybeSingle();

				if (!error && data && typeof data === 'object' && 'status' in data) {
					const fetchedStatus = (data as any).status as ShowAttendanceStatus;
					setStatus(fetchedStatus);
				} else {
					setStatus(null);
				}
			} catch (error) {
				console.error("Error fetching initial show attendance:", error);
				setStatus(null);
			} finally {
				setIsLoading(false);
			}
		}

		fetchInitialStatus();
	}, [userId, showId, enabled, supabase]);

	// Real-time subscription - this is where all updates come from
	useEffect(() => {
		if (!userId || !showId || !enabled) return;

		// Unique channel per instance to avoid conflicts
		const channelName = `show-attendance:${showId}:${userId}:${instanceId.current}`;

		const channel = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "show_attendance",
					filter: `user_id=eq.${userId}`,
				},
				(payload: RealtimePostgresChangesPayload<ShowAttendanceData>) => {
					// Client-side filter for show_id (Supabase Realtime only supports 1 filter)
					const record = payload.eventType === "DELETE" ? payload.old : payload.new;
					if ((record as any)?.show_id !== showId) {
						return;
					}

					// Real-time update from database
					if (payload.eventType === "DELETE") {
						setStatus(null);
					} else if (payload.new) {
						setStatus(payload.new.status);
					}
				},
			)
			.subscribe((status) => {
				// Handle subscription errors gracefully - errors are silently handled
				if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
					// Will retry on reconnect
				}
			});

		channelRef.current = channel;

		return () => {
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
			}
		};
	}, [userId, showId, enabled, supabase]);

	// Listen for optimistic updates from other button instances on the same page
	useEffect(() => {
		if (!userId || !showId) return;

		const handleOptimisticUpdate = (event: CustomEvent) => {
			const {
				userId: eventUserId,
				showId: eventShowId,
				status: eventStatus,
			} = event.detail;
			// Check if this event is for our show
			if (eventUserId === userId && eventShowId === showId) {
				setStatus(eventStatus);
			}
		};

		window.addEventListener(
			SHOW_ATTENDANCE_EVENT as any,
			handleOptimisticUpdate as any,
		);

		return () => {
			window.removeEventListener(
				SHOW_ATTENDANCE_EVENT as any,
				handleOptimisticUpdate as any,
			);
		};
	}, [userId, showId]);

	// Update status with optimistic updates
	const updateAttendance = async (newStatus: ShowAttendanceStatus | null) => {
		if (!userId) {
			toast.error("Please sign in to update show attendance");
			return;
		}

		// 1. Optimistic update - instant UI feedback
		setStatus(newStatus);

		// 2. Broadcast to other instances on the page
		const event = new CustomEvent(SHOW_ATTENDANCE_EVENT, {
			detail: {
				userId,
				showId,
				status: newStatus,
			},
		});
		window.dispatchEvent(event);

		// 3. Track analytics for attendance changes
		if (newStatus) {
			trackShowAttendance(showId, showName || "Show", venue || "", newStatus === "going" ? "going" : "interested");
		}

		// 4. Send to database
		try {
			await updateAttendanceMutation.mutateAsync({
				showId,
				status: newStatus,
			});
		} catch (error) {
			// Error handled in mutation onError
			// Real-time will correct the state if needed
		}

		// 5. Real-time will confirm/correct in ~100ms
	};

	return {
		status,
		updateAttendance,
		isLoading,
		isPending: updateAttendanceMutation.isPending,
	};
}

// Helper function for status labels
function getStatusLabel(status: ShowAttendanceStatus): string {
	switch (status) {
		case "want_to_go":
			return "Want to Go";
		case "going":
			return "Going";
		default:
			return "Want to Go";
	}
}